/**
 * Conditions for showing or not showing the "lead gen" email-signup-forms that must be evaluated asynchronously.
 *
 * Extracted from emailSignupLocalStorage.
 */

import { localStorageChecks } from './emailSignupLocalStorage.ts';

/**
 * Calls onSuccess if the things we have to check asynchronously (localStorage, CookieScript-state) say we should
 * continue.
 *
 * @param onSuccess The function that actually renders this email-signup-tool. To be shown if localStorage-checks pass.
 * @param minimizedLeadGen Whether or not we're using the "minimized" version of lead-gen.
 */
export const asyncChecks = (onSuccess: () => void, minimizedLeadGen: boolean) => {
    const cookieScriptPromise = new Promise<void>((resolve, reject) => {
        cookieScriptChecks(resolve, minimizedLeadGen);
    });

    const localStoragePromise = new Promise<void>((resolve, reject) => {
        localStorageChecks(resolve, minimizedLeadGen);
    });

    Promise.all([cookieScriptPromise, localStoragePromise]).then(onSuccess);
};

const cookieScriptChecks = (onSuccess: () => void, useMinimizedField: boolean) => {
    // If we're running the non-minimized version of lead-gen, we're free to skip CookieScript-checks.
    if (!useMinimizedField) {
        onSuccess();
        return;
    }

    // If CookieScript was fully loaded *and* a choice was made before this function was called
    if (window.CookieScript?.instance?.currentState()?.action !== undefined) {
        onSuccess();
    } else {
        // Handle case where CookieScript was not loaded before this function was called
        window.addEventListener('CookieScriptLoaded', function () {
            if (window.CookieScript?.instance?.currentState()?.action !== undefined) { // user already made consent decision; banner won't be shown
                onSuccess();
            }
        });

        // Handle case where CookieScript loaded but no choice was made before this function was called
        ['CookieScriptAcceptAll', 'CookieScriptAccept', 'CookieScriptReject', 'CookieScriptClose'].forEach((eventName) => {
            window.addEventListener(eventName, function () {
                onSuccess();
            });
        });
    }
};
